/**
 * Conectores por nome de sistema — o desenho do conector de cada módulo.
 *
 * **Medido em produção (13/09/2026), e quase tudo é uma coisa só:**
 *
 * | campo | o que há lá |
 * |---|---|
 * | 278 registros | em 263 nomes de sistema — quase um por sistema |
 * | posição | **268 são "conector lado chicote"**; 8 fotos, 2 do lado componente |
 * | contagem conferida | **6 de 278** — é o trabalho que falta |
 * | imagem | 262 SVG, 4 raster, **12 sem nenhuma** |
 * | publicado | 265 de 278 |
 * | validação e ordem | **zero registros usam** — nem o formulário antigo os tem |
 *
 * **A imagem é o conteúdo, e a API não a serve.** O arquivo mora no `~/Img` da
 * aplicação *Sistema* (o legado grava lá como `conectorNomeSistema{id}.{ext}` e
 * lê de lá), que **não é** a pasta da ApiConteudo — a raiz dela é
 * `C:\inetpub\diagweb.com.br\httpdocs\`. É por isso que `httpdocs/Img` não existe:
 * a pasta procurada nunca esteve sob `httpdocs`.
 *
 * Então esta tela cuida do **cadastro** — posição, contagem, publicação — e diz
 * onde a imagem está em vez de mostrar um quadro quebrado.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

export type ConectorNomeSistema = {
  id: number
  nomeSistemaId: number
  nomeSistema: string | null
  /** `conectorNomeSistema{id}.{ext}`. Vazio = registro sem desenho. */
  imagem: string | null
  posicao: number
  /** "Contagem validada" no formulário antigo: os pinos foram conferidos. */
  contagem: boolean
  validacao: boolean
  ordem: number
  publicado: boolean
  publicadoEm: string | null
}

/** As quatro posições do formulário antigo, com o texto de lá. */
export const POSICOES = [
  { valor: 1, rotulo: "Conector lado chicote" },
  { valor: 2, rotulo: "Conector lado componente" },
  { valor: 3, rotulo: "Foto do conector lado chicote" },
  { valor: 4, rotulo: "Foto do conector lado componente" },
] as const

export function rotuloDaPosicao(p: number) {
  return POSICOES.find((x) => x.valor === p)?.rotulo ?? `Posição ${p}`
}

export function usarConectoresNomeSistema(f: { nomeSistemaId?: number; publicado?: boolean }) {
  return useQuery({
    queryKey: ["conectores-nome-sistema", f] as const,
    staleTime: 60_000,
    queryFn: () => obter<ConectorNomeSistema[]>("api/conectores-nome-sistema", {
      nomeSistemaId: f.nomeSistemaId || undefined,
      publicado: f.publicado,
    }),
  })
}

export type DadosDoConector = {
  nomeSistemaId: number
  posicao: number
  contagem: boolean
  validacao: boolean
  ordem: number
}

/**
 * Cria o registro — **e só o registro**.
 *
 * O legado grava a linha, depois renomeia o arquivo enviado para
 * `conectorNomeSistema{id}.{ext}` e o guarda. A API ainda não aceita o binário
 * ("Upload do binário pendente da API de arquivos", diz a própria rota), então
 * o que nasce daqui é um cadastro **sem desenho** — e desenho é o conteúdo.
 *
 * A tela não esconde isso: avisa antes de criar, e marca na lista os 12 que já
 * estão nessa situação hoje.
 */
export function usarCriarConector() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (dados: DadosDoConector) => {
      const r = await api.post<ConectorNomeSistema>(
        "api/conectores-nome-sistema", dados, foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conectores-nome-sistema"] }),
  })
}

export function usarAlterarConector() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number; dados: DadosDoConector }) => {
      await api.put(`api/conectores-nome-sistema/${id}`, dados, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conectores-nome-sistema"] }),
  })
}

/** Exclui o cadastro. **O arquivo fica** — nada aqui toca no disco do legado. */
export function usarExcluirConector() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/conectores-nome-sistema/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conectores-nome-sistema"] }),
  })
}

/** Põe no ar ou tira. Rota própria, pelo mesmo motivo dos outros módulos. */
export function usarPublicarConector() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, publicar }: { id: number; publicar: boolean }) => {
      await api.post(
        `api/conectores-nome-sistema/${id}/${publicar ? "publicar" : "despublicar"}`,
        null, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conectores-nome-sistema"] }),
  })
}
